/**
 * Auth hook to decode JWT and provide user session info
 */
import { useMemo } from 'react';
import { authApi } from '@/shared/api/auth-api';

interface DecodedToken {
  userId: number;
  telegramUserId: string;
  firstName: string;
  lastName?: string;
  username?: string;
  role: string;
  dbUserId: string;
  iat: number;
  exp: number;
}

interface AuthSession {
  token: string | null;
  isAuthenticated: boolean;
  user: {
    userId: number;
    telegramUserId: string;
    firstName: string;
    lastName?: string;
    username?: string;
    role: string;
    dbUserId: string;
  } | null;
  isAdmin: boolean;
  logout: () => void;
}

/**
 * Decode JWT payload (supports both JWT and legacy base64 tokens)
 */
function decodeJWT(token: string): DecodedToken | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length === 3) {
      // Standard JWT — decode payload (2nd part), handle base64url
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    }
    // Legacy base64 token (pre v4.1)
    return JSON.parse(atob(token));
  } catch {
    return null;
  }
}

/**
 * Hook to access current auth session
 */
export function useAuth(): AuthSession {
  const token = authApi.getToken();
  const isAuthenticated = authApi.isAuthenticated();

  const decoded = useMemo(() => {
    if (!token) return null;
    return decodeJWT(token);
  }, [token]);

  const user = useMemo(() => {
    if (!decoded) return null;

    return {
      userId: decoded.userId,
      telegramUserId: decoded.telegramUserId,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
      username: decoded.username,
      role: decoded.role,
      dbUserId: decoded.dbUserId,
    };
  }, [decoded]);

  const isAdmin = useMemo(() => {
    return user?.role === 'admin';
  }, [user]);

  return {
    token,
    isAuthenticated,
    user,
    isAdmin,
    logout: authApi.logout,
  };
}
